import React, { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";

const CustomDropdown = ({
  options = [],
  value = null,
  onChange,
  placeholder = "Select an option",
  disabled = false,
  className = "",
  name = "",
  id = "",
  required = false,
  renderOption = null, // Custom render function for options
  getDisplayValue = null, // Custom function to get display value
  renderValue = null, // Custom render function for the selected value (returns JSX)
  searchable = false, // Enable search functionality
  ...props
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedValue, setSelectedValue] = useState(value);
  const [searchQuery, setSearchQuery] = useState("");
  const dropdownRef = useRef(null);
  const menuRef = useRef(null);
  const searchInputRef = useRef(null);

  useEffect(() => {
    setSelectedValue(value);
  }, [value]);

  // Position dropdown menu on mobile
  useEffect(() => {
    if (isOpen && dropdownRef.current) {
      const isMobile = window.innerWidth <= 768;
      if (isMobile) {
        // Prevent body scroll when dropdown is open on mobile
        const originalOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        
        // Use multiple attempts to ensure menu is rendered in portal before positioning
        const positionMenu = () => {
          const trigger = dropdownRef.current?.querySelector('.custom-dropdown-trigger');
          const menu = menuRef.current;
          
          if (!trigger) {
            return false;
          }
          
          if (!menu) {
            // Menu ref not set yet, try again
            return false;
          }
          
          // Check if menu is actually in the DOM (for portal)
          const menuInDOM = document.body.contains(menu) || menu.parentElement === document.body;
          if (!menuInDOM) {
            // Menu not in DOM yet, wait a bit
            return false;
          }
          
          const rect = trigger.getBoundingClientRect();
          const viewportWidth = window.innerWidth;
          const viewportHeight = window.innerHeight;
          const padding = 20; // Padding from screen edges
          
          // Calculate menu width - match trigger width but ensure it fits
          let menuWidth = Math.max(rect.width, 200); // Minimum width
          let menuLeft = rect.left;
          
          // Ensure menu doesn't go off right edge
          if (menuLeft + menuWidth > viewportWidth - padding) {
            menuLeft = Math.max(padding, viewportWidth - menuWidth - padding);
          }
          
          // Ensure menu doesn't go off left edge
          if (menuLeft < padding) {
            menuLeft = padding;
            // If we had to adjust left, also adjust width to fit
            menuWidth = Math.min(menuWidth, viewportWidth - (padding * 2));
          }
          
          // Ensure menuLeft is never 0 or negative - CRITICAL FIX
          if (menuLeft <= 0 || menuLeft < padding) {
            menuLeft = padding;
            menuWidth = Math.min(menuWidth, viewportWidth - (padding * 2));
          }
          
          // If trigger is at left edge, position menu from left edge with padding
          if (rect.left < padding) {
            menuLeft = padding;
            menuWidth = Math.min(rect.width || 300, viewportWidth - (padding * 2));
          }
          
          // Calculate menu top position
          let menuTop = rect.bottom + 4;
          
          // Force a reflow to get accurate scrollHeight
          menu.style.display = 'block';
          menu.style.visibility = 'hidden';
          const menuHeight = Math.min(200, menu.scrollHeight || 200);
          menu.style.visibility = 'visible';
          
          // Check if menu would go off bottom of screen
          if (menuTop + menuHeight > viewportHeight - padding) {
            // Try to position above trigger instead
            const spaceAbove = rect.top - padding;
            if (spaceAbove > menuHeight) {
              menuTop = rect.top - menuHeight - 4;
            } else {
              // Not enough space above, position at bottom of screen with max height
              menuTop = viewportHeight - Math.min(menuHeight, viewportHeight - padding * 2) - padding;
            }
          }
          
          // Ensure menuLeft is valid (not 0 or negative) - ABSOLUTE REQUIREMENT
          const finalLeft = Math.max(padding, menuLeft);
          const finalTop = Math.max(padding, menuTop);
          const finalWidth = Math.max(200, Math.min(menuWidth, viewportWidth - (padding * 2)));
          
          // CRITICAL: Never allow left to be 0 - use setProperty with !important
          const safeLeft = Math.max(padding, finalLeft);
          menu.style.setProperty('left', `${safeLeft}px`, 'important');
          
          // Apply styles with !important to prevent CSS overrides
          menu.style.setProperty('position', 'fixed', 'important');
          menu.style.setProperty('top', `${finalTop}px`, 'important');
          menu.style.setProperty('width', `${finalWidth}px`, 'important');
          menu.style.setProperty('max-width', `${viewportWidth - (padding * 2)}px`, 'important');
          menu.style.setProperty('min-width', '200px', 'important');
          menu.style.setProperty('z-index', '10000', 'important');
          menu.style.setProperty('visibility', 'visible', 'important');
          menu.style.setProperty('opacity', '1', 'important');
          menu.style.setProperty('display', 'block', 'important');
          menu.style.setProperty('pointer-events', 'auto', 'important');
          
          // Force background to ensure visibility
          menu.style.background = 'rgba(18, 18, 18, 0.98)';
          menu.style.backdropFilter = 'blur(10px)';
          menu.style.border = '1px solid rgba(255, 255, 255, 0.25)';
          menu.style.borderRadius = '5px';
          menu.style.boxShadow = '0px 16px 24px rgba(0, 0, 0, 0.14), 0px 6px 30px rgba(0, 0, 0, 0.12), 0px 8px 10px rgba(0, 0, 0, 0.2)';
          
          // Verify left position one more time - use !important to override any CSS
          const currentLeft = parseFloat(menu.style.left);
          if (isNaN(currentLeft) || currentLeft <= 0) {
            const centerLeft = (viewportWidth - finalWidth) / 2;
            menu.style.setProperty('left', `${Math.max(padding, centerLeft)}px`, 'important');
          } else {
            // Ensure the style is actually applied with !important
            menu.style.setProperty('left', menu.style.left, 'important');
          }
          
          // Force all critical styles with !important to prevent CSS overrides
          menu.style.setProperty('position', 'fixed', 'important');
          menu.style.setProperty('z-index', '10000', 'important');
          menu.style.setProperty('visibility', 'visible', 'important');
          menu.style.setProperty('opacity', '1', 'important');
          menu.style.setProperty('display', 'block', 'important');
          
          // Verify the position is actually applied
          requestAnimationFrame(() => {
            const verifyRect = menu.getBoundingClientRect();
            if (verifyRect.left <= 0 || verifyRect.width === 0) {
              // Last resort: center it
              const lastResortLeft = (window.innerWidth - 300) / 2;
              menu.style.setProperty('left', `${Math.max(20, lastResortLeft)}px`, 'important');
              menu.style.setProperty('width', '300px', 'important');
              menu.style.setProperty('height', '200px', 'important');
            }
          });
          
          // Verify menu is actually visible and has dimensions
          const menuRect = menu.getBoundingClientRect();
          if (menuRect.width === 0 || menuRect.height === 0) {
            // Menu might not be properly sized, force dimensions
            if (menuRect.width === 0) {
              menu.style.width = `${Math.min(menuWidth, viewportWidth - padding * 2)}px`;
            }
            if (menuRect.height === 0 && menu.scrollHeight > 0) {
              menu.style.height = `${Math.min(menu.scrollHeight, 200)}px`;
            }
          }
          
          // Final check - if menu is still not visible, use fallback positioning
          const finalRect = menu.getBoundingClientRect();
          const isOffScreen = finalRect.width === 0 || finalRect.height === 0 || 
              finalRect.top < 0 || finalRect.left < 0 ||
              finalRect.top > viewportHeight || finalRect.left > viewportWidth;
              
          if (isOffScreen) {
            // Fallback: center the menu on screen with guaranteed visibility
            const fallbackWidth = Math.min(300, viewportWidth - padding * 2);
            const fallbackHeight = Math.min(200, viewportHeight - padding * 2);
            menu.style.left = `${(viewportWidth - fallbackWidth) / 2}px`;
            menu.style.top = `${Math.max(padding, (viewportHeight - fallbackHeight) / 2)}px`;
            menu.style.width = `${fallbackWidth}px`;
            menu.style.height = `${fallbackHeight}px`;
            menu.style.overflowY = 'auto';
          }
          
          // Always ensure menu is visible
          menu.style.visibility = 'visible';
          menu.style.opacity = '1';
          menu.style.display = 'block';
          
          return true;
        };
        
        // Position with multiple attempts to ensure menu is rendered
        let attempts = 0;
        const maxAttempts = 15;
        let positioned = false; // Track if we've successfully positioned
        
        const tryPosition = () => {
          if (positioned) return; // Don't reposition if already done
          
          attempts++;
          const success = positionMenu();
          if (success) {
            positioned = true;
            // Success! Menu is positioned - verify it's visible
            const menu = menuRef.current;
            if (menu) {
              const rect = menu.getBoundingClientRect();
              
              // Final validation - ensure menu is actually visible
              if (rect.left <= 0 || rect.width === 0 || rect.height === 0) {
                // Still not visible, use guaranteed fallback
                const viewportWidth = window.innerWidth;
                const viewportHeight = window.innerHeight;
                const padding = 20;
                const fallbackWidth = Math.min(300, viewportWidth - padding * 2);
                const fallbackHeight = Math.min(200, viewportHeight - padding * 2);
                
                menu.style.left = `${(viewportWidth - fallbackWidth) / 2}px`;
                menu.style.top = `${Math.max(padding, (viewportHeight - fallbackHeight) / 2)}px`;
                menu.style.width = `${fallbackWidth}px`;
                menu.style.height = `${fallbackHeight}px`;
                menu.style.overflowY = 'auto';
                menu.style.background = 'rgba(18, 18, 18, 0.98)';
                menu.style.backdropFilter = 'blur(10px)';
                menu.style.border = '1px solid rgba(255, 255, 255, 0.25)';
                menu.style.borderRadius = '5px';
              } else {
              }
            }
          } else if (attempts < maxAttempts) {
            // Try again after a short delay
            setTimeout(tryPosition, 30);
          }
        };
        
        // Start positioning attempts with various delays
        tryPosition();
        requestAnimationFrame(() => {
          tryPosition();
        });
        setTimeout(() => {
          tryPosition();
        }, 50);
        setTimeout(() => {
          tryPosition();
        }, 100);
        
        // Cleanup: restore body overflow when dropdown closes
        return () => {
          document.body.style.overflow = originalOverflow;
        };
      } else {
        // Reset to absolute positioning on desktop
        const menu = menuRef.current;
        if (menu) {
          menu.style.position = '';
          menu.style.top = '';
          menu.style.left = '';
          menu.style.width = '';
          menu.style.maxWidth = '';
          menu.style.minWidth = '';
          menu.style.zIndex = '';
          menu.style.visibility = '';
          menu.style.opacity = '';
          menu.style.display = '';
          menu.style.pointerEvents = '';
        }
      }
    }
  }, [isOpen]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Small delay to allow option clicks to process first
      setTimeout(() => {
        // Check if click is outside both the dropdown trigger and the menu
        const isClickOutsideDropdown = dropdownRef.current && !dropdownRef.current.contains(event.target);
        const isClickOutsideMenu = menuRef.current && !menuRef.current.contains(event.target);
        
        // Also check if the click is on an option (which should select it, not close)
        const isClickOnOption = event.target.closest('.custom-dropdown-option');
        
        // Don't close if menu is not yet positioned (give it time to appear)
        const menu = menuRef.current;
        const menuReady = menu && (
          menu.style.position === 'fixed' || 
          menu.getBoundingClientRect().width > 0
        );
        
        if (isClickOutsideDropdown && isClickOutsideMenu && !isClickOnOption && isOpen && menuReady) {
          setIsOpen(false);
        }
      }, 50); // Increased delay to allow menu to appear
    };

    if (isOpen) {
      // Use capture phase to catch events early, but with delay for option clicks
      document.addEventListener("mousedown", handleClickOutside, true);
      document.addEventListener("touchstart", handleClickOutside, true);
      
      // Also handle window resize to close dropdown
      const handleResize = () => {
        if (isOpen) {
          setIsOpen(false);
        }
      };
      window.addEventListener("resize", handleResize);
      
      return () => {
        document.removeEventListener("mousedown", handleClickOutside, true);
        document.removeEventListener("touchstart", handleClickOutside, true);
        window.removeEventListener("resize", handleResize);
      };
    }

    return () => {
      // Cleanup
    };
  }, [isOpen]);

  const handleToggle = () => {
    if (!disabled) {
      setIsOpen(!isOpen);
      if (!isOpen && searchable) {
        // Focus search input when opening
        setTimeout(() => {
          if (searchInputRef.current) {
            searchInputRef.current.focus();
          }
        }, 0);
      } else if (!isOpen) {
        setSearchQuery("");
      }
    }
  };

  const handleSelect = (option, event) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
      // Stop immediate propagation to prevent click-outside handler from firing
      if (event.nativeEvent) {
        event.nativeEvent.stopImmediatePropagation();
      }
    }
    
    // Close dropdown first to prevent any interference
    setIsOpen(false);
    setSearchQuery("");
    
    // Update state immediately - use functional update to ensure latest state
    setSelectedValue(() => option);
    
    // Call onChange callback to notify parent (for controlled components)
    if (onChange) {
      onChange(option);
    }
  };

  // Filter options based on search query
  const filteredOptions = searchable && searchQuery
    ? options.filter((option) => {
        const optionLabel = typeof option === "object" ? option.label : option;
        const optionValue = typeof option === "object" ? option.value : option;
        const searchLower = searchQuery.toLowerCase();
        
        // Check label and value
        const matchesLabel = optionLabel && optionLabel.toString().toLowerCase().includes(searchLower);
        const matchesValue = optionValue && optionValue.toString().toLowerCase().includes(searchLower);
        
        // Also search within data object if it exists (for account numbers, TRADING_ID, etc.)
        let matchesData = false;
        if (typeof option === "object" && option.data) {
          const data = option.data;
          // Search in common fields that might contain searchable text
          const searchableFields = [
            data.TRADING_ID,
            data.account?.account_information?.TRADING_ID,
            data.ID,
            data.id,
            data.NAME,
            data.name,
            data.CURRENCY_CODE,
            data.currencies?.CURRENCY_CODE,
            data.CURRENCY_SYMBOL,
            data.currencies?.CURRENCY_SYMBOL
          ];
          
          matchesData = searchableFields.some(field => {
            if (field !== null && field !== undefined) {
              return field.toString().toLowerCase().includes(searchLower);
            }
            return false;
          });
        }
        
        return matchesLabel || matchesValue || matchesData;
      })
    : options;

  const getDisplayValueFunc = () => {
    // Use value prop if provided (controlled component), otherwise use selectedValue (uncontrolled)
    const currentValue = value !== null && value !== undefined ? value : selectedValue;
    
    if (getDisplayValue && typeof getDisplayValue === 'function') {
      return getDisplayValue(currentValue);
    }
    
    if (!currentValue) return placeholder;
    
    // If option is an object with label/value, show label
    if (typeof currentValue === "object" && currentValue.label) {
      return currentValue.label;
    }
    
    // If option is an object with value property, show value
    if (typeof currentValue === "object" && currentValue.value !== undefined) {
      return currentValue.value;
    }
    
    // Otherwise show the value directly
    return currentValue;
  };

  // Render menu content
  const renderMenu = () => {
    if (!isOpen) return null;
    
    const isMobile = typeof window !== 'undefined' && window.innerWidth <= 768;
    
    // Set initial styles for mobile portal rendering
    const initialStyles = isMobile ? {
      position: 'fixed',
      zIndex: 10000,
      visibility: 'visible',
      opacity: 1,
      display: 'block',
      pointerEvents: 'auto'
    } : {};
    
    const menuContent = (
      <div 
        className="custom-dropdown-menu" 
        ref={menuRef}
        style={{
          ...initialStyles,
          // Ensure menu is always visible when rendered
          background: 'rgba(18, 18, 18, 0.98)',
          border: '1px solid rgba(255, 255, 255, 0.25)',
          borderRadius: '5px',
          boxShadow: '0px 16px 24px rgba(0, 0, 0, 0.14), 0px 6px 30px rgba(0, 0, 0, 0.12), 0px 8px 10px rgba(0, 0, 0, 0.2)',
        }}
      >
        {searchable && (
          <div className="custom-dropdown-search">
            <i className="fa-solid fa-search custom-dropdown-search-icon"></i>
            <input
              ref={searchInputRef}
              type="text"
              className="custom-dropdown-search-input"
              placeholder="Search..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onClick={(e) => e.stopPropagation()}
              onKeyDown={(e) => {
                if (e.key === "Escape") {
                  setIsOpen(false);
                  setSearchQuery("");
                }
              }}
            />
          </div>
        )}
        {filteredOptions.length === 0 ? (
          <div className="custom-dropdown-empty">No options available</div>
        ) : (
          filteredOptions.map((option, index) => {
            const optionValue = typeof option === "object" ? option.value : option;
            const optionLabel = typeof option === "object" ? option.label : option;
            const isSelected =
              (typeof selectedValue === "object" && selectedValue?.value === optionValue) ||
              selectedValue === optionValue ||
              selectedValue === option;

            if (renderOption) {
              return (
                <div
                  key={index}
                  className={`custom-dropdown-option ${isSelected ? "custom-dropdown-option-selected" : ""}`}
                  onClick={(e) => handleSelect(option, e)}
                  onTouchEnd={(e) => {
                    e.preventDefault();
                    handleSelect(option, e);
                  }}
                >
                  {renderOption(option, isSelected)}
                </div>
              );
            }

            return (
              <div
                key={index}
                className={`custom-dropdown-option ${isSelected ? "custom-dropdown-option-selected" : ""}`}
                onClick={(e) => handleSelect(option, e)}
                onTouchEnd={(e) => {
                  e.preventDefault();
                  handleSelect(option, e);
                }}
              >
                <span className="custom-dropdown-option-text" dir="auto">{optionLabel}</span>
                {isSelected && <i className="fa-solid fa-check custom-dropdown-check"></i>}
              </div>
            );
          })
        )}
      </div>
    );

    // On mobile, use portal to render menu to body
    if (isMobile) {
      // Ensure document.body exists
      if (typeof document !== 'undefined' && document.body) {
        return createPortal(menuContent, document.body);
      } else {
        // Fallback: render normally
        return menuContent;
      }
    }

    // On desktop, render normally
    return menuContent;
  };

  return (
    <div
      className={`custom-dropdown ${className} ${isOpen ? "custom-dropdown-open" : ""}`}
      ref={dropdownRef}
      {...props}
    >
      <button
        type="button"
        className="custom-dropdown-trigger"
        onClick={handleToggle}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
      >
        <span className="custom-dropdown-value">
          {renderValue ? (
            (value !== null && value !== undefined ? value : selectedValue) ? 
              renderValue((value !== null && value !== undefined ? value : selectedValue), placeholder) : 
              <span className="custom-dropdown-placeholder">{placeholder}</span>
          ) : (
            <span className="custom-dropdown-value-text" dir="auto">{getDisplayValueFunc()}</span>
          )}
        </span>
        <i className={`fa-solid fa-chevron-${isOpen ? "up" : "down"} custom-dropdown-arrow`}></i>
      </button>

      {renderMenu()}
    </div>
  );
};

export default CustomDropdown;

